import { renderToStaticMarkup } from "react-dom/server";
import type { ReactElement } from "react";
import { CompileCollector, CompileContext } from "./context.js";

export type CompileOptions = {
  title?: string;
  gsapUrl?: string;
};

function escapeScriptText(value: string): string {
  return value.replace(/<\/script/gi, "<\\/script");
}

function markTransitionVisual(markup: string, sequenceId: string): string {
  const sequenceStart = markup.indexOf(`<div id="${sequenceId}"`);
  if (sequenceStart < 0) return markup;
  const visual = '<div class="hf-sequence-visual"';
  const visualStart = markup.indexOf(visual, sequenceStart);
  if (visualStart < 0) return markup;
  const insertAt = visualStart + visual.length;
  return `${markup.slice(0, insertAt)} data-layout-allow-overflow data-layout-allow-overlap data-layout-allow-occlusion${markup.slice(insertAt)}`;
}

export function compileComposition(element: ReactElement, options: CompileOptions = {}): string {
  const collector = new CompileCollector();
  let markup = renderToStaticMarkup(
    <CompileContext.Provider value={collector}>{element}</CompileContext.Provider>,
  );
  collector.validate();

  for (const transition of [...collector.typeMatchCuts, ...collector.whipPanCuts, ...collector.zoomThroughTransitions]) {
    markup = markTransitionVisual(markup, transition.from);
    markup = markTransitionVisual(markup, transition.to);
  }

  const composition = collector.composition!;
  const transitionCss = collector.dissolves
    .map((transition) => `#${transition.to}>.hf-sequence-visual{opacity:0}`)
    .concat(collector.slides.map((transition) => `#${transition.from}>.hf-sequence-visual,#${transition.to}>.hf-sequence-visual{will-change:transform,opacity}#${transition.to}>.hf-sequence-visual{opacity:0}`))
    .concat(collector.fadeThroughs.map((transition) => `#${transition.to}>.hf-sequence-visual{opacity:0}`))
    .concat(collector.typeMatchCuts.map((transition) => `#${transition.to}>.hf-sequence-visual{opacity:0;transform-origin:50% 50%;will-change:transform,border-radius}`))
    .concat(collector.whipPanCuts.map((transition) => `#${transition.from}>.hf-sequence-visual,#${transition.to}>.hf-sequence-visual{filter:blur(var(--hf-whip-blur,0px));will-change:transform,filter,opacity}#${transition.to}>.hf-sequence-visual{opacity:0}`))
    .concat(collector.zoomThroughTransitions.map((transition) => `#${transition.to}>.hf-sequence-visual{opacity:0;clip-path:inset(0 var(--hf-zoom-clip-right,100%) 0 0);will-change:transform,opacity,clip-path}`))
    .join("");
  const initializerCode = collector.initializers.join("\n      ");
  const timelineCode = collector.operations.map((operation) => operation.code).join("\n      ");
  const title = options.title ?? composition.id;
  const gsapUrl = options.gsapUrl ?? "https://cdn.jsdelivr.net/npm/gsap@3.14.2/dist/gsap.min.js";

  return `<!doctype html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <meta name="viewport" content="width=${composition.width}, height=${composition.height}" />
    <title>${title}</title>
    <script src="${gsapUrl}"></script>
    <style>
      *{box-sizing:border-box}html,body{margin:0;width:${composition.width}px;height:${composition.height}px;overflow:hidden;background:#000}#root{position:relative;width:${composition.width}px;height:${composition.height}px;overflow:hidden}.hf-sequence,.hf-sequence-visual{position:absolute;inset:0;overflow:hidden}.hf-fade-through-wash{position:absolute;inset:0;z-index:900;opacity:0;pointer-events:none}${transitionCss}
    </style>
  </head>
  <body>
    ${markup}
    <script>
      gsap.defaults({ immediateRender: false });
      window.__timelines = window.__timelines || {};
      const tl = gsap.timeline({ paused: true });
      ${escapeScriptText(initializerCode)}
      ${escapeScriptText(timelineCode)}
      tl.seek(0);
      window.__timelines[${JSON.stringify(composition.id)}] = tl;
    </script>
  </body>
</html>
`;
}
